/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Ring2All Team
 📄 File: hooks/useAuditLogList.ts
 🗓️ Created: 2025-12-02
 🧩 Description:
    Hook for managing Audit Logs list
────────────────────────────────────────────────────────────────*/

import { useCallback, useMemo, useState } from 'react';
import { useI18n } from '../../../hooks/useI18n';
import { useDataGrid } from '../../../hooks/useDataGrid';
import { useNotifications } from '../../../contexts/NotificationContext';
import { fetchAuditLogs } from '../api/auditLogApi';
import { AuditLog, ListAuditLogsParams, AuditLogFilterValues } from '../types/auditLog.types';

/**
 * Hook for managing Audit Logs list
 */
export function useAuditLogList() {
  const { t } = useI18n();
  const { showError } = useNotifications();

  // Filter state
  const [filters, setFilters] = useState<AuditLogFilterValues>({
    startDate: '',
    endDate: '',
    actions: [],
    resource: '',
    userId: '',
    domainId: '',
  });

  const fetchData = useCallback(async (params: {
    page: number;
    limit: number;
    sortBy: string;
    sortOrder: 'asc' | 'desc';
    search?: string;
  }) => {
    try {
      const queryParams: ListAuditLogsParams = {
        page: params.page,
        limit: params.limit,
        sortBy: params.sortBy,
        sortOrder: params.sortOrder,
        search: params.search,
        // Add filters to query params
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
        action: filters.actions.length > 0 ? filters.actions.join(',') : undefined,
        resource: filters.resource || undefined,
        userId: filters.userId || undefined,
        domainId: filters.domainId ? parseInt(filters.domainId, 10) : undefined,
      };

      const result = await fetchAuditLogs(queryParams);
      
      return {
        data: result.data,
        pagination: result.pagination
      };
    } catch (error) {
      const message = error instanceof Error ? error.message : t('auditLogs.messages.loadError');
      showError(message);
      throw error;
    }
  }, [showError, t, filters]);

  const availableColumns = useMemo(() => [
    { key: 'id', label: t('auditLogs.columns.id'), visible: false, sortable: false },
    { key: 'createdAt', label: t('auditLogs.columns.timestamp'), visible: true, sortable: true },
    { key: 'action', label: t('auditLogs.columns.action'), visible: true, sortable: true },
    { key: 'resource', label: t('auditLogs.columns.resource'), visible: true, sortable: true },
    { key: 'resourceId', label: t('auditLogs.columns.resourceId'), visible: false, sortable: false },
    { key: 'user', label: t('auditLogs.columns.user'), visible: true, sortable: false },
    { key: 'ipAddress', label: t('auditLogs.columns.ipAddress'), visible: true, sortable: false },
    { key: 'userAgent', label: t('auditLogs.columns.userAgent'), visible: false, sortable: false },
    { key: 'metadata', label: t('auditLogs.columns.metadata'), visible: false, sortable: false },
  ], [t]);

  const {
    data,
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh
  } = useDataGrid({
    fetchData,
    initialColumns: availableColumns,
    initialSortBy: 'createdAt',
    initialSortOrder: 'desc',
    initialLimit: 50,
    moduleName: 'auditLogs'
  });

  // Filter handlers
  const handleFilterChange = useCallback((newFilters: AuditLogFilterValues) => {
    setFilters(newFilters);
  }, []);

  const handleClearFilters = useCallback(() => {
    setFilters({
      startDate: '',
      endDate: '',
      actions: [],
      resource: '',
      userId: '',
      domainId: '',
    });
    setTimeout(() => refresh(), 100);
  }, [refresh]);

  const handleApplyFilters = useCallback(() => {
    refresh();
  }, [refresh]);

  return {
    data: data as AuditLog[],
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    filters,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    handleFilterChange,
    handleClearFilters,
    handleApplyFilters,
    refresh
  };
}

